// Configurações (canvas E10 e 7.8), pela engrenagem do perfil: Conta, com o celular e Trocar, Notificações no celular
// (uma chave por tipo e os avisos pelo WhatsApp), o app na tela de início, Mesa, com a chave "Zoeira na mesa",
// Privacidade, com o ranking da cidade e do estado e a chave do WhatsApp entre amigos, e Bloqueados, com
// Desbloquear. Desbloquear não refaz a amizade: um novo pedido segue as regras de sempre.
import { useEffect, useId, useState } from 'react';
import { desbloquear, lerBloqueados } from '../comum/amigos.ts';
import { ErroDaApi } from '../comum/api.ts';
import { avisarNaTela } from '../comum/aviso.tsx';
import { comRanking, conta, type PerfilConta, type Sessao } from '../comum/conta.ts';
import { ChaveRankingCidade } from '../comum/RankingDaCidade.tsx';
import { type MeuCelular, celularValido, formatarCelular, guardarMeuCelular, lerMeuCelular, mudarAvisosWhatsapp } from '../comum/celular.ts';
import { CampoCelular } from '../comum/FolhaCelular.tsx';
import { definirRelacao } from '../comum/relacoes.ts';
import { ir, voltar } from '../comum/rotas.ts';
import { Rosto } from '../comum/Rosto.tsx';
import { ChaveWhatsapp } from '../comum/Whatsapp.tsx';
import { SecaoInstalar, SecaoPush } from '../comum/AvisosNoCelular.tsx';
import { Tela, Topo } from './comum.tsx';

type Bloqueado = { usuario: string; apelido: string; avatar: string | null; em: number };

function Bloqueados({ sessao }: { sessao: Sessao }) {
  const [lista, setLista] = useState<Bloqueado[] | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    lerBloqueados(sessao).then((r) => { if (vivo) setLista(r.bloqueados); }, () => { if (vivo) setLista([]); });
    return () => { vivo = false; };
  }, [sessao.id]);
  async function soltar(b: Bloqueado) {
    setOcupado(b.usuario);
    try {
      const r = await desbloquear(sessao, b.usuario);
      definirRelacao(b.usuario, r.estado);
      setLista((l) => l?.filter((x) => x.usuario !== b.usuario) ?? null);
      avisarNaTela(`${b.apelido} foi desbloqueado.`);
    } catch (e) { avisarNaTela(e instanceof ErroDaApi ? e.message : 'Não deu certo. Tente de novo.'); } finally { setOcupado(null); }
  }
  return (
    <section className="secao-amigos" aria-label="Bloqueados">
      <h2 className="rotulo">BLOQUEADOS{lista && lista.length > 0 && <span className="dourado"> · {lista.length}</span>}</h2>
      {!lista && <p className="carregando" role="status">Carregando...</p>}
      {lista && lista.length === 0 && <p className="nota">Ninguém bloqueado. Quem você bloquear aparece aqui, para desbloquear quando quiser.</p>}
      {lista && lista.length > 0 && (
        <ul className="lista-pessoas">
          {lista.map((b) => (
            <li key={b.usuario}>
              <Rosto perfil={{ apelido: b.apelido, avatar: b.avatar }} usuario={b.usuario} cor="var(--line-600)" tamanho={40} />
              <span className="nome"><b>{b.apelido}</b></span>
              <button className="btn2 desbloquear" disabled={ocupado === b.usuario} aria-label={`Desbloquear ${b.apelido}`} onClick={() => void soltar(b)}>Desbloquear</button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// O ranking da cidade e do estado: a mesma chave do Perfil. Sem cidade, o atalho para escolher a cidade.
function RankingDaCidade({ sessao, perfil, aoMudarPerfil }: { sessao: Sessao; perfil: PerfilConta; aoMudarPerfil?: (p: PerfilConta) => void }) {
  const [ocupado, setOcupado] = useState(false);
  if (!perfil.cidadeIbge) {
    return (
      <button className="cartao linha-plano" onClick={() => ir({ tela: 'perfil', campo: 'cidade' })}>
        <span className="texto-chave"><b>Ranking da cidade e do estado</b><small>Escolha a cidade no Perfil. O Brasil vale sempre</small></span>
      </button>
    );
  }
  async function mudar(ligado: boolean) {
    setOcupado(true);
    try {
      await conta.rankingRegional(sessao, ligado);
      aoMudarPerfil?.(comRanking(perfil, ligado));
    } catch (e) { avisarNaTela(e instanceof ErroDaApi ? e.message : 'Não deu certo. Tente de novo.'); } finally { setOcupado(false); }
  }
  return <ChaveRankingCidade codigo={perfil.cidadeIbge} ligado={!!perfil.rankingRegionalEm} configuracoes ocupado={ocupado} aoMudar={(l) => void mudar(l)} />;
}

// "Zoeira na mesa": ligada para todo mundo. Desligada, a aba Zoeira some das Reações e a zoeira dos outros não chega;
// na mesa, vale a partir da próxima mão.
function ChaveZoeira({ sessao, perfil, aoMudarPerfil }: { sessao: Sessao; perfil: PerfilConta; aoMudarPerfil?: (p: PerfilConta) => void }) {
  const id = useId();
  const [ocupado, setOcupado] = useState(false);
  const ligada = perfil.zoeira !== false;
  async function mudar(l: boolean) {
    setOcupado(true);
    try {
      await conta.zoeira(sessao, l);
      aoMudarPerfil?.({ ...perfil, zoeira: l });
    } catch (e) { avisarNaTela(e instanceof ErroDaApi ? e.message : 'Não deu certo. Tente de novo.'); } finally { setOcupado(false); }
  }
  return (
    <section className="secao-amigos" aria-label="Mesa">
      <h2 className="rotulo">MESA</h2>
      <section className={'cartao chave-ranking' + (ligada ? ' on' : '')} aria-label="Zoeira na mesa">
        <div className="linha-chave">
          <span className="icone-whats icone-zoeira" aria-hidden="true">😜</span>
          <span className="texto-chave">
            <b id={id + 'r'}>Zoeira na mesa</b>
            <small id={id + 'a'}>Frases de zoeira dos outros jogadores aparecem sobre o rosto de quem mandou. Desligada, você não vê nem manda zoeira.</small>
          </span>
          <button type="button" role="switch" aria-checked={ligada} aria-labelledby={id + 'r'} aria-describedby={id + 'a'} disabled={ocupado}
            className={'chave-whats' + (ligada ? ' on' : '')} onClick={() => void mudar(!ligada)}>
            <span className="bolinha" />
          </button>
        </div>
      </section>
    </section>
  );
}

// Conta: o celular, por inteiro para o dono, com Trocar. A troca usa a mesma validação, o mesmo limite de tentativas e
// a mesma regra de número único do primeiro acesso.
function SecaoConta({ sessao, cel, aoMudar }: { sessao: Sessao; cel: MeuCelular; aoMudar: (c: MeuCelular) => void }) {
  const [trocando, setTrocando] = useState(false);
  const [numero, setNumero] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  async function salvar(ev: React.FormEvent) {
    ev.preventDefault();
    if (!celularValido(numero)) return;
    setOcupado(true); setErro(null);
    try {
      aoMudar(await guardarMeuCelular(sessao, numero));
      setTrocando(false); setNumero('');
      avisarNaTela('Celular salvo.');
    } catch (e) { setErro(e instanceof ErroDaApi ? e.message : 'Não deu certo. Tente de novo.'); } finally { setOcupado(false); }
  }
  return (
    <section className="secao-amigos conta-celular" aria-label="Conta">
      <h2 className="rotulo">CONTA</h2>
      {/* O e-mail da conta Google, só leitura: é com ele que a pessoa entra. */}
      {sessao.email && (
        <div className="cartao linha-email-conta">
          <span className="texto-chave"><b>E-mail</b><small>{sessao.email}</small></span>
        </div>
      )}
      {trocando ? (
        <form className="cartao form-celular" onSubmit={(ev) => void salvar(ev)}>
          <CampoCelular valor={numero} aoMudar={(v) => { setNumero(v); setErro(null); }} erro={erro} autoFocus
            ajuda="Ninguém na mesa vê o seu número. Cada número fica em uma conta só." />
          <div className="botoes-celular">
            <button className="btn1" type="submit" disabled={ocupado || !celularValido(numero)}>{ocupado ? 'UM INSTANTE...' : 'SALVAR'}</button>
            <button className="btn2" type="button" disabled={ocupado} onClick={() => { setTrocando(false); setNumero(''); setErro(null); }}>Cancelar</button>
          </div>
        </form>
      ) : (
        <div className="cartao linha-celular-conta">
          <span className="texto-chave">
            <b>Celular</b>
            <small>{cel.celular ? formatarCelular(cel.celular) : 'Sem celular. Informe para sentar na mesa.'}</small>
          </span>
          <button className="btn2 trocar-celular" onClick={() => setTrocando(true)}>{cel.celular ? 'Trocar' : 'Informar'}</button>
        </div>
      )}
      <p className="nota">Como usamos o seu número: <a href="/privacidade">Política de privacidade</a>.</p>
    </section>
  );
}

// Avisos pelo WhatsApp: o consentimento da caixa do primeiro acesso, com a data guardada. Sem celular, fica desligada.
function ChaveAvisosWhatsapp({ sessao, cel, aoMudar }: { sessao: Sessao; cel: MeuCelular; aoMudar: (c: MeuCelular) => void }) {
  const id = useId();
  const [ocupado, setOcupado] = useState(false);
  async function mudar(ligado: boolean) {
    setOcupado(true);
    try { aoMudar(await mudarAvisosWhatsapp(sessao, ligado)); } catch (e) {
      avisarNaTela(e instanceof ErroDaApi ? e.message : 'Não deu certo. Tente de novo.');
    } finally { setOcupado(false); }
  }
  return (
    <ul className="cartao tipos-push avisos-whatsapp">
      <li>
        <span className="texto-chave">
          <b id={id + 'r'}>Avisos pelo WhatsApp</b>
          <small id={id + 'a'}>{cel.celular ? 'Avisos e convites do Truco de 6 no seu celular. Desligue quando quiser.' : 'Informe o celular na seção Conta.'}</small>
        </span>
        <button role="switch" aria-checked={cel.avisosWhatsapp} aria-labelledby={id + 'r'} aria-describedby={id + 'a'} className={'chave-whats' + (cel.avisosWhatsapp ? ' on' : '')}
          disabled={ocupado || !cel.celular} onClick={() => void mudar(!cel.avisosWhatsapp)}><span className="bolinha" /></button>
      </li>
    </ul>
  );
}

export function Configuracoes({ sessao, perfil, aoMudarPerfil }: { sessao: Sessao; perfil: PerfilConta; aoMudarPerfil?: (p: PerfilConta) => void }) {
  // O celular da conta, só para conta Google: a seção Conta e a chave dos avisos pelo WhatsApp usam o mesmo valor.
  const [cel, setCel] = useState<MeuCelular | null>(null);
  useEffect(() => {
    let vivo = true;
    if (!sessao.anonimo) lerMeuCelular(sessao).then((c) => { if (vivo) setCel(c); }, () => {});
    return () => { vivo = false; };
  }, [sessao.id]);
  return (
    <Tela>
      <Topo titulo="Configurações" voltar={voltar} />
      {/* A partir de 720 px, um formulário em 2 colunas: a conta e os avisos à esquerda; a privacidade à direita. */}
      <div className="duas-colunas">
        <div className="col">
          {cel && <SecaoConta sessao={sessao} cel={cel} aoMudar={setCel} />}
          <SecaoPush sessao={sessao}>{cel && <ChaveAvisosWhatsapp sessao={sessao} cel={cel} aoMudar={setCel} />}</SecaoPush>
          <SecaoInstalar />
        </div>
        <div className="col">
          <ChaveZoeira sessao={sessao} perfil={perfil} aoMudarPerfil={aoMudarPerfil} />
          <section className="secao-amigos" aria-label="Privacidade">
            <h2 className="rotulo">PRIVACIDADE</h2>
            <RankingDaCidade sessao={sessao} perfil={perfil} aoMudarPerfil={aoMudarPerfil} />
            {sessao.anonimo
              ? <p className="cartao vazio-lista">Guarde sua conta com Google para mostrar o WhatsApp aos amigos e bloquear jogadores.</p>
              : <ChaveWhatsapp key={cel?.celular ?? ''} sessao={sessao} />}
          </section>
          {!sessao.anonimo && <Bloqueados sessao={sessao} />}
          <p className="nota links-legais centro"><a href="/privacidade">Política de privacidade</a></p>
        </div>
      </div>
    </Tela>
  );
}
